import { Injectable } from '@angular/core';
import { from } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Entry, createClient } from 'contentful';
import { IProjectFields, INewsFields } from '../../core/_models/content-types';
const CONFIG = environment.contentful_config;

@Injectable({
	providedIn: 'root'
})
export class ContentfulService {

	private client = createClient({
		space: CONFIG.space,
		accessToken: CONFIG.accessToken,
		environment: CONFIG.environment
	});
	titleHandlers: Function[];

	constructor() {
		this.titleHandlers = [];
	}

	onTitleChange(fn: any): void {
		this.titleHandlers.push(fn);
	}

	/**
	 * Retrieves the About Page content from Contentful.
	 * @returns A promise that resolves to the first About Page entry.
	 */
	async getAboutPageContent(): Promise<Entry<any> | undefined> {
		return this.client.getEntries({
			content_type: CONFIG.contentTypeIds.aboutPage
		})
			.then(res => res.items[0]);
	}

	/**
	 * Retrieves the Work Page content from Contentful.
	 * @returns A promise that resolves to the first Work Page entry.
	 */
	async getWorkPageContent(): Promise<Entry<any> | undefined> {
		const res = await this.client.getEntries({
			content_type: CONFIG.contentTypeIds.workPage
		});
		return res.items[0];
	}

	/**
	 * Retrieves the Contact Page content from Contentful.
	 * @returns A promise that resolves to the first Contact Page entry, or undefined if not found.
	 */
	async getContactPageContent(): Promise<Entry<any> | undefined> {
		const res = await this.client.getEntries({
			content_type: CONFIG.contentTypeIds.contactPage
		});
		return res.items && res.items.length > 0 ? res.items[0] : undefined;
	}

	/**
	 * Retrieves the Home Page content from Contentful.
	 * @returns A promise that resolves to the first Home Page entry, or undefined if not found.
	 */
	async getHomePageContent(): Promise<Entry<any> | undefined> {
		const res = await this.client.getEntries({
			content_type: CONFIG.contentTypeIds.homePage
		});
		return res.items[0];
	}

	//
	// Capabilities Page Content
	//
	async getCapabilitiesPageContent(): Promise<Entry<any>> {
		const res = await this.client.getEntries({
			content_type: CONFIG.contentTypeIds.capabilitiesPage
		});
		return res.items[0];
	}

	//
	// Careers Page Content
	//
	async getCareersPageContent(): Promise<Entry<any>> {
		const res = await this.client.getEntries({
			content_type: CONFIG.contentTypeIds.careersPage
		});
		return res.items[0];
	}

	//
	// Privacy Policy Page Content
	//
	async getPrivacyPolicyPageContent(): Promise<Entry<any>> {
		const res = await this.client.getEntries({
			content_type: CONFIG.contentTypeIds.privacyPolicyPage
		});
		return res.items[0];
	}

	//
	// Terms of Use Page Content
	//
	async getTermsOfUsePageContent(): Promise<Entry<any>> {
		const res = await this.client.getEntries({
			content_type: CONFIG.contentTypeIds.termsOfUsePage
		});
		return res.items[0];
	}

	//
	// News Page Content
	//
	async getNewsPageContent(): Promise<Entry<any>> {
		const res = await this.client.getEntries({
			content_type: CONFIG.contentTypeIds.newsPage
		});
		return res.items[0];
	}

	//
	// Get All News Items
	//
	async getNews(query?: object): Promise<Entry<any>[]> {
		const res = await this.client.getEntries(Object.assign({
			content_type: 'news'
		}, query));
		return res.items;
	}

	//
	// Get Single news item by ID
	//
	async getNewsItemById(newsId: string, query?: any): Promise<Entry<INewsFields>> {
		return await this.client.getEntry<INewsFields>(newsId, query);
	}

	/**
	 * Fetches news items in the "Work" category from Contentful.
	 * @param query Optional query parameters to filter results.
	 * @returns A promise of news entries in the "Work" category.
	 */
	async getNewsCategoryWork(query?: object): Promise<Entry<any>[]> {
		const res = await this.client.getEntries<INewsFields>(Object.assign({
			content_type: 'news',
			'fields.category': 'Work',
			order: '-sys.createdAt'
		}, query));
		return res.items;
	}

	/**
	 * Fetches news items in the "People" category from Contentful.
	 * @param query Optional query parameters to filter results.
	 * @returns An observable of news entries in the "People" category.
	 */
	async getNewsCategoryPeople(query?: object): Promise<Entry<any>[]> {
		const res = await this.client.getEntries<INewsFields>(Object.assign({
			content_type: 'news',
			'fields.category': 'People',
			order: '-sys.createdAt'
		}, query));
		return res.items;
	}

	/**
	 * Fetches news items in the "Perspectives" category from Contentful.
	 * @param query Optional query parameters to filter results.
	 * @returns An observable of news entries in the "Perspectives" category.
	 */
	async getNewsCategoryPerspectives(query?: object): Promise<Entry<any>[]> {
		const res = await this.client.getEntries<INewsFields>(Object.assign({
			content_type: 'news',
			'fields.category': 'Perspectives',
			order: '-sys.createdAt'
		}, query));
		return res.items;
	}

	/**
	 * Fetches news items in the "Awards" category from Contentful.
	 * @param query Optional query parameters to filter results.
	 * @returns A promise resolving to an array of news entries (`Entry<any>[]`) in the "Awards" category.
	 */
	async getNewsCategoryAwards(query?: object): Promise<Entry<any>[]> {
		const res = await this.client.getEntries<INewsFields>(Object.assign({
			content_type: 'news',
			'fields.category': 'Awards',
			order: '-sys.createdAt'
		}, query));
		return res.items;
	}

	/**
	 * Fetches news items in the "Bites" category from Contentful.
	 * @param query Optional query parameters to filter results.
	 * @returns A promise of news entries in the "Bites" category.
	 */
	async getNewsCategoryBites(query?: object): Promise<Entry<any>[]> {
		const res = await this.client.getEntries<INewsFields>(Object.assign({
			content_type: 'news',
			'fields.category': 'Bites',
			order: '-sys.createdAt'
		}, query));
		return res.items;
	}


	/**
	 * Retrieves project entries (work) from Contentful.
	 * @param query Optional query parameters to filter results.
	 * @returns A promise resolving to the list of project entries.
	 */
	async getProjects(query?: object) {
		return this.client.getEntries<IProjectFields>(Object.assign({
			content_type: 'work',
			order: '-sys.createdAt'
		}, query));
	}


	//
	// Get Single Work/Project Item By ID
	//

	async getProjectById(projectId: string, query?: any) {
		return await this.client.getEntry<IProjectFields>(projectId, query);
	}

	//
	// Fetch Next Work/Project Items
	//

	getNextWorkItem(query?: object) {
		return from(
			this.client.getEntries<IProjectFields>(Object.assign({
				content_type: 'work',
				order: '-sys.createdAt',
				// limit: 1,
				// skip: 1
			}, query))
		);
	}

}


